<template>
    <div class="flex flex-col items-center">
        <div class="space-y-3 w-full">
            <div
                v-for="option in options"
                :key="option.id"
                class="bg-theme-card rounded-xl p-5 border-2 transition-all"
                :class="cardClasses(option)"
                @click="selectOption(option.id)"
            >
                <div class="flex items-center justify-between">
                    <div>
                        <h3 class="font-semibold text-lg text-theme-base">
                            {{ option.title }}
                        </h3>
                        <p class="text-sm text-theme-muted">
                            {{ option.description }}
                        </p>
                    </div>
                    <div
                        class="w-14 h-14 rounded-lg flex items-center justify-center text-2xl"
                    >
                        {{ option.icon }}
                    </div>
                </div>

                <custom-collapse :visible="selected === option.id">
                    <ul
                        class="mt-3 space-y-2 border-t border-theme-border pt-3"
                    >
                        <li
                            v-for="feature in option.features"
                            :key="feature.text"
                            class="flex items-start gap-2 text-sm"
                        >
                            <span class="flex-shrink-0">{{
                                feature.icon
                            }}</span>
                            <span class="text-theme-muted">
                                {{ feature.text }}
                            </span>
                        </li>
                    </ul>
                </custom-collapse>
            </div>
        </div>

        <p
            v-if="touched && !selected && !readOnly"
            class="text-theme-danger text-sm mt-2"
        >
            Selecciona una opción para continuar
        </p>
        <p v-if="error" class="text-theme-danger text-sm mt-2">
            {{ error }}
        </p>
    </div>
</template>

<script>
import onboardingCompletedGuard from "@/mixins/onboardingCompletedGuard";
import onboardingStepNavigation from "@/mixins/onboardingStepNavigation";
import { mapActions, mapState } from "vuex";

import CustomCollapse from "@/components/utils/CustomCollapse.vue";

const ACCENT_CLASSES = {
    blue: {
        border: "border-brand-blue",
        text: "text-brand-blue",
        hoverBorder: "hover:border-brand-blue"
    },
    red: {
        border: "border-brand-red",
        text: "text-brand-red",
        hoverBorder: "hover:border-brand-red"
    },
    magenta: {
        border: "border-brand-magenta",
        text: "text-brand-magenta",
        hoverBorder: "hover:border-brand-magenta"
    },
    purple: {
        border: "border-brand-purple",
        text: "text-brand-purple",
        hoverBorder: "hover:border-brand-purple"
    },
    light: {
        border: "border-brand-light",
        text: "text-brand-light",
        hoverBorder: "hover:border-brand-light"
    }
};

export default {
    name: "OnboardingBusinessTypePage",

    components: { CustomCollapse },

    mixins: [onboardingCompletedGuard, onboardingStepNavigation],

    data() {
        return {
            stepKey: "business-type",
            selected: null,
            touched: false,
            error: null,
            isLoading: false,
            options: [
                {
                    id: "share_only",
                    accentColor: "blue",
                    title: "Profesional",
                    description: "Tu marca personal en una sola página",
                    icon: "👩‍💻",
                    features: [
                        {
                            icon: "📸",
                            text: "Tu foto, tu historia y tus redes en un solo link"
                        },
                        {
                            icon: "🔗",
                            text: "Enlaces a tus talleres, cursos o asesorías"
                        },
                        {
                            icon: "🧱",
                            text: "Arma tu página con bloques: un video, tu ubicación, testimonios"
                        },
                        {
                            icon: "💬",
                            text: "Que te escriban directo por WhatsApp"
                        },
                        {
                            icon: "🎯",
                            text: "Ideal para creadores de contenido, coaches y profesionales independientes"
                        }
                    ]
                },
                {
                    id: "organization",
                    accentColor: "light",
                    title: "Organización",
                    description: "Muestra quién eres y qué ofreces",
                    icon: "🏛️",
                    features: [
                        {
                            icon: "🏛️",
                            text: "Tu misión, tus servicios y cómo contactarte, en una sola página"
                        },
                        {
                            icon: "🔗",
                            text: "Enlaces a tus programas, convocatorias y talleres"
                        },
                        {
                            icon: "🧱",
                            text: "Arma tu página con bloques: videos, ubicación, testimonios"
                        },
                        {
                            icon: "📝",
                            text: "Una página aparte para cada programa o servicio"
                        },
                        {
                            icon: "🎯",
                            text: "Ideal para fundaciones, academias, entidades públicas y empresas de servicios"
                        }
                    ]
                },
                {
                    id: "social_seller",
                    accentColor: "red",
                    title: "Tienda en línea",
                    description: "Convierte tus seguidores en clientes",
                    icon: "🛒",
                    features: [
                        {
                            icon: "📦",
                            text: "Catálogo con fotos y precios, listo para tu bio"
                        },
                        {
                            icon: "💬",
                            text: "Recibe pedidos y coordina la entrega con tu cliente"
                        },
                        {
                            icon: "🎯",
                            text: "Ideal si vendes por redes o haces cada producto por pedido"
                        }
                    ]
                },
                {
                    id: "single_store",
                    accentColor: "magenta",
                    title: "Tienda física",
                    description: "Tu local, también en línea",
                    icon: "🏪",
                    features: [
                        {
                            icon: "📦",
                            text: "Tu catálogo en línea con lo que tienes en tu punto de venta"
                        },
                        {
                            icon: "🧾",
                            text: "Registra tus ventas y pedidos en un solo lugar"
                        },
                        {
                            icon: "🎯",
                            text: "Ideal si tienes un solo local o sede"
                        }
                    ]
                },
                {
                    id: "multi_store",
                    accentColor: "purple",
                    title: "Sucursales",
                    description: "Todas tus sedes en un solo lugar",
                    icon: "🏬",
                    features: [
                        {
                            icon: "🏬",
                            text: "Un catálogo para todas tus sedes"
                        },
                        {
                            icon: "📊",
                            text: "Inventario independiente por sucursal"
                        },
                        {
                            icon: "💲",
                            text: "Precios por sucursal, distintos de los de tu tienda en línea si lo necesitas"
                        },
                        {
                            icon: "👥",
                            text: "Cada persona de tu equipo entra con su propio usuario"
                        },
                        {
                            icon: "🎯",
                            text: "Ideal para franquicias y cadenas con varias sedes"
                        }
                    ]
                }
            ]
        };
    },

    computed: {
        ...mapState("onboarding", ["businessType"]),
        localStepperUi() {
            return {
                nextDisabled: !this.selected,
                loading: this.isLoading,
                nextLabel: "",
                nextVariant: "primary"
            };
        }
    },

    async mounted() {
        await this.ensureOnboardingStatus();
        if (this.redirectIfOnboardingCompleted()) return;
        if (this.readOnly) {
            const saved = this.selections.businessType || this.businessType;
            if (saved) {
                this.selected = saved;
            }
        }
    },

    methods: {
        ...mapActions("onboarding", ["submitBusinessType"]),
        ...mapActions("auth", ["applyAllPermissions"]),

        selectOption(id) {
            if (this.readOnly) return;
            this.selected = id;
        },

        cardClasses(option) {
            const accent = ACCENT_CLASSES[option.accentColor];
            const isSelected = this.selected === option.id;

            const colorClasses = isSelected
                ? [accent.border, accent.text]
                : ["border-theme-border", "text-theme-base"];

            const interactionClasses = this.readOnly
                ? isSelected
                    ? ["cursor-default"]
                    : ["cursor-default", "opacity-50"]
                : ["cursor-pointer", accent.hoverBorder];

            return [...colorClasses, ...interactionClasses];
        },

        stepperNext() {
            if (this.readOnly) {
                this.goToNextReachable();
                return;
            }
            this.continueStep();
        },

        async continueStep() {
            if (this.readOnly) return;
            this.touched = true;
            if (!this.selected || this.isLoading) return;

            this.isLoading = true;
            this.error = null;
            try {
                const data = await this.submitBusinessType({
                    businessType: this.selected
                });

                if (data.permissions) {
                    this.applyAllPermissions({
                        permissions: data.permissions
                    });
                }

                this.navigateByFlow(data.onboardingFlow);
                this.isLoading = false;
            } catch (err) {
                this.isLoading = false;
                this.error =
                    err.message || "Error al guardar el tipo de negocio";
            }
        },

        navigateByFlow(flow) {
            if (!flow) return;
            if (flow.status === "completed" && !flow.nextStep) {
                this.$router.push(this.completionRoute);
                return;
            }
            const nextRoute =
                flow.status === "completed" && flow.nextStep
                    ? this.routeForStep(flow.nextStep)
                    : this.routeForStep(flow.step);
            if (nextRoute) {
                this.$router.push(nextRoute);
            }
        }
    }
};
</script>
